import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
  CModal,
  CFormTextarea,
} from "@coreui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrders,
  fetchShippingPartners,
  updateOrderStatus,
  updateShippingPartner,
  updateTrackingNumber,
} from "../../features/seller/sellerSlice";
import AppLoader from "../../components/seller/AppLoader";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AppToast from "../../components/seller/AppToast";
import { formatCurrency, formatPrice } from "../../utils/price";

import { useNavigate, useSearchParams } from "react-router-dom";
import { cilReload } from "@coreui/icons";
import CIcon from "@coreui/icons-react";

const trackingValidationSchema = Yup.object({
  trackingInput: Yup.string().required("Tracking number is required"),
  shippingPartner: Yup.string().required("Shipping partner is required"),
  expectedDelivery: Yup.date().required("Expected delivery date is required"),
  consignmentNote: Yup.string().required("Consignment note is required"),
});

// Mapping Helpers
// READ (GET orders API) → FORM
const mapOrderToForm = (order) => ({
  trackingInput: order?.shipping_detail?.seller_tracking_number || "",
  shippingPartner: order?.shipping_detail?.seller_shipping_partner || "",
  consignmentNote: order?.shipping_detail?.seller_consignment_note || "",
  expectedDelivery: order?.shipping_detail?.seller_expected_delivery_date
    ? order.shipping_detail.seller_expected_delivery_date.split("T")[0]
    : "",
});

// FORM → WRITE (POST update API)
const mapFormToPayload = (values, orderId) => ({
  order_id: orderId,
  tracking_number: values.trackingInput,
  shipping_partner: values.shippingPartner,
  consignment_note: values.consignmentNote,
  expected_delivery: values.expectedDelivery,
});

const ViewOrders = () => {
  const dispatch = useDispatch();
  const {
    list: orders,

    loading,
  } = useSelector((state) => state.seller.orders);
  const shipping = useSelector((state) => state.seller.shipping.partners);
  console.log("orders list:", orders);

  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [visibleModal, setVisibleModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [page, setPage] = useState(1);
  const [searchParams] = useSearchParams();
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  const [shippingDaysModal, setShippingDaysModal] = useState(false);
  const [shippingDays, setShippingDays] = useState("");
  const [statusOrder, setStatusOrder] = useState(null);

  useEffect(() => {
    const statusFromUrl = searchParams.get("status");

    if (statusFromUrl) {
      setFilters((prev) => ({
        ...prev,
        status: statusFromUrl,
      }));
    }
  }, [searchParams]);

  const [filters, setFilters] = useState({
    product: "",
    status: "",
    payment_status: "",
  });

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const today = new Date().toISOString().split("T")[0];

  // console.log("Order list:", orders);
  // console.log("Current page:", currentPage);
  // console.log("Last page:", lastPage);
  // console.log("Shipping:", shipping);

  useEffect(() => {
    dispatch(fetchOrders())
      .unwrap()
      .catch((err) => {
        console.error("Failed to load orders:", err);
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchShippingPartners())
      .unwrap()
      .catch((err) => {
        console.error("Failed to load shipping partners:", err);
      });
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [search, filters, entriesPerPage]);

  const filtered = useMemo(() => {
    const query = search.toLowerCase();

    return orders.filter((o) => {
      const matchesSearch = query
        ? [o.unique_order_id, o.product?.name, o.buyer?.name]
            .filter(Boolean)
            .some((v) => v.toString().toLowerCase().includes(query))
        : true;

      const matchesProduct = filters.product
        ? o.product?.name === filters.product
        : true;

      const matchesStatus = filters.status ? o.status === filters.status : true;

      const matchesPayment = filters.payment_status
        ? o.payment_status === filters.payment_status
        : true;

      return matchesSearch && matchesProduct && matchesStatus && matchesPayment;
    });
  }, [orders, search, filters]);

  /* ---------------- Sorting ---------------- */
  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      const aVal =
        sortConfig.key.split(".").reduce((acc, k) => acc?.[k], a) ?? "";

      const bVal =
        sortConfig.key.split(".").reduce((acc, k) => acc?.[k], b) ?? "";

      const valA = typeof aVal === "string" ? aVal.toLowerCase() : aVal;
      const valB = typeof bVal === "string" ? bVal.toLowerCase() : bVal;

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortConfig]);

  const indexOfLast = page * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;

  const currentData = sorted.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(sorted.length / entriesPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const products = [
    ...new Set(orders.map((o) => o.product?.name).filter(Boolean)),
  ];
  const statuses = [...new Set(orders.map((o) => o.status).filter(Boolean))];
  console.log("Status:", statuses);
  const paymentStatuses = [
    ...new Set(orders.map((o) => o.payment_status).filter(Boolean)),
  ];

  const handleResetFilters = () => {
    setFilters({
      product: "",
      status: "",
      payment_status: "",
    });

    setSearch("");
    setSortConfig({ key: null, direction: "asc" });
    setPage(1);
  };

  const handleOpenModal = (order) => {
    setSelectedOrder(order);
    setVisibleModal(true);
  };

  // console.log("selectedOrder.id", selectedOrder ? selectedOrder.id : null);
  const handleUpdateTracking = async (values) => {
    if (!selectedOrder) return;

    try {
      const payload = mapFormToPayload(values, selectedOrder.id);
      console.log("Payload:", payload);
      const res = await dispatch(updateTrackingNumber(payload)).unwrap();

      setVisibleModal(false);
      dispatch(fetchOrders());

      setToastType("success");
      setToastMessage(res?.message || "Tracking number updated successfully.");
      setToastVisible(true);
    } catch (err) {
      console.error("Failed to update tracking:", err);
      setToastType("error");
      setToastMessage(err?.message || "Failed to update tracking number.");
      setToastVisible(true);
    }
  };

  const handleUpdateShippingPartner = async (orderId, partnerId) => {
    // console.log("partnerId:", partnerId);
    try {
      const res = await dispatch(
        updateShippingPartner({
          id: orderId,
          data: { shipping_partner_id: partnerId },
        })
      ).unwrap();
      dispatch(fetchOrders());

      setToastType("success");
      setToastMessage(res?.message || "Shipping partner updated successfully.");
      setToastVisible(true);
    } catch (err) {
      console.error("Failed to update shipping partner", err);
      setToastType("error");
      setToastMessage(err?.message || "Failed to update shipping partner.");
      setToastVisible(true);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      const res = await dispatch(
        updateOrderStatus({
          id: orderId,
          data: { status },
        })
      ).unwrap();
      dispatch(fetchOrders());

      setToastType("success");
      setToastMessage(res?.message || "Order status updated successfully.");
      setToastVisible(true);
    } catch (err) {
      console.error("Failed to update order status", err);
      setToastType("error");
      setToastMessage(err?.message || "Failed to update order status.");
      setToastVisible(true);
    }
  };

  const handleConfirmShipped = async () => {
    if (!shippingDays || isNaN(shippingDays)) {
      setToastType("error");
      setToastMessage("Please enter valid shipping days.");
      setToastVisible(true);
      return;
    }

    try {
      const res = await dispatch(
        updateOrderStatus({
          id: statusOrder.id,
          data: {
            status: "shipped",
            shipping_duration: Number(shippingDays),
          },
        })
      ).unwrap();

      setShippingDaysModal(false);
      setStatusOrder(null);
      dispatch(fetchOrders());

      setToastType("success");
      setToastMessage(res?.message || "Order marked as shipped.");
      setToastVisible(true);
    } catch (err) {
      setToastType("error");
      setToastMessage(err?.message || "Failed to update status.");
      setToastVisible(true);
    }
  };

  // Navigation hook for row click order detail
  const navigate = useNavigate();

  const handleRowClick = (orderId) => {
    navigate(`/seller/order-detail/${orderId}`);
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const ORDER_STATUS_LABELS = {
    pending: "Pending",
    accepted: "Accepted",
    matched: "Matched",
    shipped: "Shipped",
    delivered: "Delivered",
    completed: "Completed",
    rejected: "Rejected",

    admin_review: "Admin Review",
    awaiting_extra_payment: "Awaiting Extra Payment",
  };

  const getOrderStatusLabel = (status) => {
    if (!status) return "-";

    return (
      ORDER_STATUS_LABELS[status] ||
      status
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  };

  const ORDER_STATUS_COLORS = {
    pending: "warning",
    accepted: "info",
    matched: "primary",
    shipped: "info",
    picked: "info",
    delivered: "success",
    completed: "success",
    rejected: "danger",
    cancelled: "danger",

    admin_review: "secondary",
    awaiting_extra_payment: "warning",
  };

  const getOrderStatusColor = (status) => {
    return ORDER_STATUS_COLORS[status] || "secondary";
  };

  if (loading) {
    return <AppLoader />;
  }
  return (
    <>
      <div className="container-fluid px-3">
        <CRow className="mt-4 mb-3">
          <CCol>
            <h4 className="mb-0">Received Orders</h4>
          </CCol>
        </CRow>

        <CCard>
          <CCardHeader>
            <CRow className="mb-2">
              <CCol>
                <h6
                  className="text-muted mb-0 d-flex align-items-center"
                  style={{ height: "100%" }}
                >
                  Orders placed by buyers
                </h6>
              </CCol>
            </CRow>

            <hr className="my-2" />
            <CRow className="g-2 mb-2 align-items-center">
              {/* Product filter */}
              <CCol md={3}>
                <CFormSelect
                  size="sm"
                  value={filters.product}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, product: e.target.value }))
                  }
                >
                  <option value="">Products</option>
                  {products.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>

              {/* Order status filter */}
              <CCol md={3}>
                <CFormSelect
                  size="sm"
                  value={filters.status}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <option value="">Order Status</option>
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {getOrderStatusLabel(s)}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>

              {/* Payment status filter */}
              <CCol md={3}>
                <CFormSelect
                  size="sm"
                  value={filters.payment_status}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      payment_status: e.target.value,
                    }))
                  }
                >
                  <option value="">Payment Status</option>
                  {paymentStatuses.map((p) => (
                    <option key={p} value={p}>
                      {capitalizeFirst(p)}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>
              {/* Reset filters button */}
              <CCol md={3} className="text-end">
                <CButton
                  color="secondary"
                  className="d-flex align-items-center justify-content-center rounded"
                  style={{
                    width: "35px",
                    height: "35px",
                    padding: 0,
                    background: "rgba(108,117,125,0.15)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(108,117,125,0.4)",
                    color: "#6c757d",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                    transition: "all 0.25s ease",
                  }}
                  onClick={handleResetFilters}
                  title="Reset filters"
                >
                  <CIcon icon={cilReload} size="sm" className="text-dark" />
                </CButton>
              </CCol>
            </CRow>

            <CRow className="align-items-center">
              <CCol md={6}>
                Show{" "}
                <CFormSelect
                  size="sm"
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  style={{ display: "inline-block", width: "auto" }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </CFormSelect>{" "}
                entries
              </CCol>
              <CCol md={6} className="text-end">
                Search:{" "}
                <CFormInput
                  type="text"
                  size="sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ display: "inline-block", width: "200px" }}
                />
              </CCol>
            </CRow>
          </CCardHeader>

          <CCardBody>
            <CTable responsive bordered hover>
              <CTableHead color="light">
                <CTableRow>
                  <CTableHeaderCell>#</CTableHeaderCell>

                  <CTableHeaderCell
                    onClick={() => handleSort("unique_order_id")}
                    role="button"
                  >
                    Order Id {renderSortIcon("unique_order_id")}
                  </CTableHeaderCell>

                  <CTableHeaderCell
                    onClick={() => handleSort("buyer.name")}
                    role="button"
                  >
                    Buyer {renderSortIcon("buyer")}
                  </CTableHeaderCell>

                  <CTableHeaderCell
                    onClick={() => handleSort("product.name")}
                    role="button"
                  >
                    Product {renderSortIcon("product")}
                  </CTableHeaderCell>

                  <CTableHeaderCell
                    onClick={() => handleSort("total_price")}
                    role="button"
                  >
                    Total Price (AUD) {renderSortIcon("total_price")}
                  </CTableHeaderCell>

                  <CTableHeaderCell
                    onClick={() => handleSort("payment_status")}
                    role="button"
                  >
                    Payment {renderSortIcon("payment_status")}
                  </CTableHeaderCell>

                  <CTableHeaderCell
                    onClick={() => handleSort("created_at")}
                    role="button"
                  >
                    Date {renderSortIcon("created_at")}
                  </CTableHeaderCell>

                  <CTableHeaderCell>Shipping Details</CTableHeaderCell>

                  <CTableHeaderCell
                    onClick={() => handleSort("status")}
                    role="button"
                  >
                    Status {renderSortIcon("status")}
                  </CTableHeaderCell>

                  <CTableHeaderCell>Action</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {currentData.map((order, index) => (
                  <CTableRow
                    key={order.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(order.id)}
                  >
                    <CTableDataCell>{indexOfFirst + index + 1}</CTableDataCell>
                    <CTableDataCell>
                      {order.unique_order_id || "-"}
                    </CTableDataCell>
                    <CTableDataCell>{order.buyer?.name || "-"}</CTableDataCell>
                    <CTableDataCell>
                      {order.product?.name || "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      {formatCurrency(order.total_price)}
                      {/* {order.total_price != null
                        ? `A$ ${formatPrice(order.total_price)}`
                        : "-"} */}
                    </CTableDataCell>
                    <CTableDataCell>
                      {order.payment_status
                        ? capitalizeFirst(order.payment_status)
                        : "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      {order.created_at ? timeAgo(order.created_at) : "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CButton
                        size="sm"
                        color="dark"
                        disabled={
                          order.status !== "accepted" &&
                          order.status !== "pending" &&
                          order.status !== "matched"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(order);
                        }}
                      >
                        Update
                      </CButton>
                    </CTableDataCell>
                    {/* <CTableDataCell>
                        <CFormSelect
                          size="sm"
                          value={order.shipping_partner_id || ""}
                          onChange={(e) =>
                            handleUpdateShippingPartner(order.id, e.target.value)
                          }
                        >
                          <option value="">Select</option>
                          {shipping &&
                            shipping.map((partner) => (
                              <option key={partner.id} value={partner.id}>
                                {partner.name}
                              </option>
                            ))}
                        </CFormSelect>
                      </CTableDataCell> */}
                    <CTableDataCell>
                      <span
                        className={`badge bg-${getOrderStatusColor(order.status)}`}
                      >
                        {getOrderStatusLabel(order.status)}
                      </span>
                    </CTableDataCell>

                    <CTableDataCell style={{ minWidth: "140px" }}>
                      <CFormSelect
                        size="sm"
                        value={order.status || ""}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          const newStatus = e.target.value;

                          if (newStatus === "shipped") {
                            setStatusOrder(order);
                            setShippingDays("");
                            setShippingDaysModal(true);
                          } else {
                            handleUpdateOrderStatus(order.id, newStatus);
                          }
                        }}
                      >
                        <option value="pending">Pending</option>
                        <option value="accepted">Accepted</option>
                        <option value="shipped">Shipped</option>
                        <option value="picked">Picked Up</option>
                        <option value="delivered">Delivered</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="rejected">Rejected</option>
                      </CFormSelect>
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {filtered.length === 0 && (
                  <CTableRow>
                    <CTableDataCell
                      colSpan="10"
                      className="text-center text-muted"
                    >
                      No Orders found.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>

            {/* {console.log("Total entries:", total)} */}

            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Showing {sorted.length === 0 ? 0 : indexOfFirst + 1} to{" "}
                {Math.min(indexOfLast, sorted.length)} of {sorted.length}{" "}
                entries
              </div>

              <div className="d-flex align-items-center">
                <CButton
                  size="sm"
                  color="light"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                  className="me-2"
                >
                  Prev
                </CButton>
                {[...Array(totalPages)].map((_, i) => (
                  <CButton
                    key={i}
                    size="sm"
                    color={page === i + 1 ? "primary" : "light"}
                    onClick={() => setPage(i + 1)}
                    className="me-2"
                  >
                    {i + 1}
                  </CButton>
                ))}
                <CButton
                  size="sm"
                  color="light"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>

        {/* Tracking Number Modal */}
        <CModal
          visible={visibleModal}
          onClose={() => setVisibleModal(false)}
          alignment="center"
        >
          {selectedOrder && (
            <CCard className="m-0">
              <CCardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Update Tracking Number</h5>
                  <CButton
                    color="light"
                    className="p-0"
                    onClick={() => setVisibleModal(false)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </CButton>
                </div>
                <Formik
                  initialValues={mapOrderToForm(selectedOrder)}
                  validationSchema={trackingValidationSchema}
                  onSubmit={handleUpdateTracking}
                  enableReinitialize
                >
                  {({
                    values,
                    setFieldValue,
                    handleBlur,
                    touched,
                    errors,
                    isSubmitting,
                  }) => (
                    <Form>
                      <div className="mb-3">
                        <CFormInput
                          label="Tracking Number"
                          name="trackingInput"
                          value={values.trackingInput}
                          onChange={(e) =>
                            setFieldValue("trackingInput", e.target.value)
                          }
                          onBlur={handleBlur}
                          placeholder="Enter tracking number"
                        />
                        {touched.trackingInput && errors.trackingInput && (
                          <div className="text-danger">
                            {errors.trackingInput}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        <CFormInput
                          label="Shipping Partner"
                          value={values.shippingPartner}
                          onChange={(e) =>
                            setFieldValue("shippingPartner", e.target.value)
                          }
                          onBlur={handleBlur}
                          placeholder="Enter shipping partner"
                        />
                        {touched.shippingPartner && errors.shippingPartner && (
                          <div className="text-danger">
                            {errors.shippingPartner}
                          </div>
                        )}
                      </div>
                      <div className="mb-3">
                        <CFormInput
                          type="date"
                          label="Expected Delivery"
                          name="expectedDelivery"
                          value={values.expectedDelivery}
                          min={today}
                          onChange={(e) =>
                            setFieldValue("expectedDelivery", e.target.value)
                          }
                          onBlur={handleBlur}
                          placeholder="Enter expected delivery date"
                        />
                        {touched.expectedDelivery &&
                          errors.expectedDelivery && (
                            <div className="text-danger">
                              {errors.expectedDelivery}
                            </div>
                          )}
                      </div>
                      <div className="mb-3">
                        <CFormTextarea
                          label="Consignment Note"
                          name="consignmentNote"
                          value={values.consignmentNote}
                          onChange={(e) =>
                            setFieldValue("consignmentNote", e.target.value)
                          }
                          onBlur={handleBlur}
                          placeholder="Enter consignment note"
                        />
                        {touched.consignmentNote && errors.consignmentNote && (
                          <div className="text-danger">
                            {errors.consignmentNote}
                          </div>
                        )}
                      </div>

                      <div className="d-flex justify-content-end mt-4">
                        <CButton
                          color="primary"
                          className="me-2"
                          type="submit"
                          disabled={isSubmitting}
                        >
                          Save
                        </CButton>
                        <CButton
                          color="secondary"
                          onClick={() => setVisibleModal(false)}
                        >
                          Cancel
                        </CButton>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CCardBody>
            </CCard>
          )}
        </CModal>

        <CModal
          visible={shippingDaysModal}
          onClose={() => setShippingDaysModal(false)}
          alignment="center"
        >
          <CCard className="m-0">
            <CCardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Shipping Details</h5>
                <CButton
                  color="light"
                  className="p-0"
                  onClick={() => setShippingDaysModal(false)}
                >
                  <span aria-hidden="true">&times;</span>
                </CButton>
              </div>

              <div className="mb-3">
                <CFormInput
                  type="number"
                  label="Shipping Days"
                  placeholder="Enter number of days"
                  value={shippingDays}
                  min={1}
                  onChange={(e) => setShippingDays(e.target.value)}
                />
              </div>

              <div className="d-flex justify-content-end mt-4">
                <CButton
                  color="primary"
                  className="me-2"
                  onClick={handleConfirmShipped}
                >
                  Confirm
                </CButton>
                <CButton
                  color="secondary"
                  onClick={() => setShippingDaysModal(false)}
                >
                  Cancel
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </CModal>
      </div>
      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};

export default ViewOrders;
